import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { ChallengeOrganizerHit } from '@radevents/shared';
import ChallengeDetailHeader from '../components/winterchallenge/ChallengeDetailHeader';
import { TeamCreateForm, TeamDraft } from '../components/winterchallenge/TeamParts';
import { messageOf } from '../components/winterchallenge/messageOf';
import { Spinner } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import challengeService from '../services/challengeService';

/**
 * `/winterchallenge/team-gruenden`. Veranstalter (freiwillig, suchen oder anlegen), Teamname und
 * Kürzel. `?verein=<id>` bringt den Veranstalter von der Veranstalterseite mit.
 */
const WinterchallengeTeamCreate: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const preselect = params.get('verein');
  const [club, setClub] = useState<ChallengeOrganizerHit | null>(null);
  const [ready, setReady] = useState(!preselect);
  const [saving, setSaving] = useState(false);

  // Vorauswahl aus der Veranstalterseite: Verein über die Suche holen, damit Zugang und
  // vorhandenes Team bekannt sind.
  useEffect(() => {
    if (!preselect || !isAuthenticated) return;
    challengeService.searchOrganizers(params.get('name') ?? '')
      .then(found => setClub(found.find(h => String(h.id) === preselect) ?? null))
      .catch(() => setClub(null))
      .finally(() => setReady(true));
  }, [preselect, isAuthenticated, params]);

  // Erst wenn die gespeicherte Anmeldung gelesen ist: sonst schickt die Seite ein angemeldetes
  // Konto beim Neuladen kurz zur Anmeldung. Der Rücksprung behält `?verein=…&name=…`.
  if (authLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner /></div>;
  }
  if (!isAuthenticated) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  const submit = async (draft: TeamDraft) => {
    setSaving(true);
    try {
      const team = await challengeService.createTeam(draft);
      toast.success('Team gegründet');
      navigate(`/winterchallenge/team/${team.slug}`);
    } catch (err) {
      toast.error(messageOf(err, 'Das Team wurde nicht gegründet.'));
      setSaving(false);
    }
  };

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet><title>Team gründen | Winterchallenge | Radsport Events</title><meta name="robots" content="noindex" /></Helmet>
      <ChallengeDetailHeader />
      <main style={{ flex: 1 }}>
        <div className="wc-page" style={{ maxWidth: 640 }}>
          {ready && (
            <TeamCreateForm club={club} saving={saving} onSearch={challengeService.searchOrganizers}
              onSubmit={submit} onCancel={() => navigate('/winterchallenge')} />
          )}
        </div>
      </main>
    </div>
  );
};

export default WinterchallengeTeamCreate;
